import {
  CUSTOMER_SERVICE_HOURS,
  HELP_CENTRE_LOCALISED_URL,
  HELP_CENTRE_URL,
} from '@seek/shared-constants';
import { useTranslations } from '@vocab/react';
import { Text, TextLink } from 'braid-design-system';
import type { FunctionComponent } from 'react';

import { useConfig } from 'src/app/shared/hooks/useConfig';
import { isSiteAnz } from 'src/app/utils/domain';
import { getPhoneNumber } from 'src/app/utils/getPhoneNumber';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes';

import translations from './.vocab';

export const ContactUs: FunctionComponent = () => {
  const { t } = useTranslations(translations);
  const { createContactUsUrl } = useLocalisedRoutes();

  const { getSite, getLanguage } = useConfig();
  const serviceHours = CUSTOMER_SERVICE_HOURS[getSite()] ?? '';
  const phoneNumber = getPhoneNumber(getSite());
  const helpCentreUrl =
    HELP_CENTRE_LOCALISED_URL[getSite()][getLanguage()] ??
    HELP_CENTRE_URL[getSite()];

  const contactUsUrl = createContactUsUrl();

  const isAnz = isSiteAnz(getSite());

  const HelpCentreInfo = () => (
    <>
      {t('help-centre-message', {
        Link: (children: React.ReactNode[]) => (
          <TextLink
            href={helpCentreUrl}
            rel="noopener noreferrer"
            target="_blank"
            title={t('help-centre-title')}
          >
            {children}
          </TextLink>
        ),
      })}
    </>
  );

  const ContactUsForAnz = () => (
    <Text>
      {t('contact-us-message-anz', {
        phoneNumber: phoneNumber || '',
        serviceHours,
        PhoneLink: (tel: React.ReactNode[]) => (
          <TextLink href={`tel:${tel}`}>{tel}</TextLink>
        ),
      })}
      <br />
      <HelpCentreInfo />
    </Text>
  );

  const ContactUsForAsia = () => (
    <Text>
      {t('contact-us-message-asia', {
        Link: (children: React.ReactNode[]) => (
          <TextLink
            href={contactUsUrl}
            rel="noopener noreferrer"
            target="_blank"
            title={t('help-centre-title')}
          >
            {children}
          </TextLink>
        ),
      })}
      <br />
      <HelpCentreInfo />
    </Text>
  );

  return isAnz ? <ContactUsForAnz /> : <ContactUsForAsia />;
};
